import { use, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  storage,
  ref,
  listAll,
  getDownloadURL,
  auth,
  firestore as db,
} from "../../../backend/firebaseconfig";
import "../../../css/Components/Pages/MembersHub/FileDirectory.css";
import Sidebar from "../../../frontend/DesignSystem/Sidebar";
import PortalCard from "../../../frontend/DesignSystem/Cards/PortalCard";
import ButtonGreen from "../../../frontend/DesignSystem/Buttons/ButtonGreen";
import { useUserEmail } from "../../../context/UserEmailContext";
import { doc, getDoc } from "firebase/firestore";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import AnnualGuide from "./WhiteLabelledDocuments/AnnualGuide/AnnualGuide";
import TaxGuide from "./WhiteLabelledDocuments/TaxGuide/TaxGuide";
import { useBranding } from "../../../context/BrandingContext";
import AnnualGuideV2 from "./WhiteLabelledDocuments/AnnualGuide/AnnualGuideV2";
import WhatIOwnAndWhereItIsKept from "./WhiteLabelledDocuments/WhatIOwnAndWhereItIsKept/WhatIOwnAndWhereItIsKept";
import generateAllTaxGuidesForAllAdvisers from "./WhiteLabelledDocuments/TaxGuide/HelperFunctions/generateAllTaxGuides";

// --- helpers ---
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});
const stripExt = (s = "") => s.replace(/\.[^.]+$/, "");

// Open a storage file ref lazily
const openFileRef = async (itemRef) => {
  try {
    const url = await getDownloadURL(itemRef);
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (e) {
    // console.error("Failed to open file:", itemRef?.fullPath, e);
  }
};

const WHITE_LABEL_SECTION_TITLE = "White Labelled Documents";
const CAN_VIEW_WHITE_LABELLED_DOCUMENTS = [
  "hrutuja.patnekar@rosemountfs.co.uk",
  // "another.person@rosemountfs.co.uk",
].map((e) => e.toLowerCase().trim());

const FileDirectory = () => {
  const { folderName = "" } = useParams();
  const navigate = useNavigate();
  // branding
  const {
    primaryColor = "081e56",
    logo = "",
    tradingName,
    regulatoryText,
  } = useBranding();

  // auth / user
  const { userEmail } = useUserEmail();
  const userName = auth.currentUser?.displayName || "";
  const normalizedEmail = userEmail?.toLowerCase().trim();

  const canSeeWhiteLabelledDocuments =
    !!normalizedEmail &&
    CAN_VIEW_WHITE_LABELLED_DOCUMENTS.includes(normalizedEmail);

  // directory state
  const [directory, setDirectory] = useState({ subfolders: [], files: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [showPDFModal, setShowPDFModal] = useState(false);
  // gated subfolders permissions
  const [permissions, setPermissions] = useState({});
  const [permLoading, setPermLoading] = useState(true);

  // org chart image
  const [imageUrl, setImageUrl] = useState(null);

  // user-specific tax guides
  const [taxGuideFiles, setTaxGuideFiles] = useState([]);

  // useEffect(() => { 
  //   generateAllTaxGuidesForAllAdvisers();
  // }, []);

  // Recursively read Firebase Storage folder to a tree
  const fetchFolderData = async (location) => {
    const folderRef = ref(storage, location);
    try {
      const { prefixes, items } = await listAll(folderRef);

      // Recurse subfolders in parallel
      const subfolders = await Promise.all(
        prefixes.map(async (p) => {
          const subfolderPath = `${location}/${p.name}`;
          const subfolderData = await fetchFolderData(subfolderPath);
          return { name: p.name, content: subfolderData };
        }),
      );

      subfolders.sort((a, b) => collator.compare(a.name, b.name));

      // Keep refs, no URL yet; sort by *display name* (basename)
      const files = items
        .map((itemRef) => ({ name: itemRef.name, ref: itemRef }))
        .sort((a, b) => collator.compare(stripExt(a.name), stripExt(b.name)));

      return { subfolders, files };
    } catch (err) {
      // console.error("Error fetching folder data:", err);
      return { subfolders: [], files: [] };
    }
  };
  // Venture Capital Trusts permissions from Firestore
  const getVTCUsers = async () => {
    const snapshot = await getDoc(
      doc(
        db,
        "Compliance",
        "Members Hub",
        "Venture Capital Trusts",
        "Permissions",
      ),
    );
    if (!snapshot.exists()) return [];
    const data = snapshot.data();
    return (data.users || []).map((e) => e?.toLowerCase().trim());
  };

  // Check access by full path
  const canSeePath = (fullPath) => {
    const allowed = permissions[fullPath];
    return !allowed || (normalizedEmail && allowed.includes(normalizedEmail));
  };

  // ---------- Effects ----------

  // Redirect if unauthenticated
  useEffect(() => {
    if (!auth.currentUser) navigate("/");
  }, [navigate]);

  // Load directory and user-specific files
  useEffect(() => {
    const initialPath = `MembersHub/${folderName}`;
    let alive = true;

    (async () => {
      setIsLoading(true);
      try {
        const data = await fetchFolderData(initialPath);
        if (alive) setDirectory(data);
      } finally {
        if (alive) setIsLoading(false);
      }

      // White Labelled Tax Guide files (if on Customer Facing Material)
      if (folderName === "Customer Facing Material" && normalizedEmail) {
        try {
          const userPath = `Authorized Members/${normalizedEmail}/White Labelled Documents/Tax Guide`;
          const { items } = await listAll(ref(storage, userPath));
          const files = items
            .map((itemRef) => ({ name: itemRef.name, ref: itemRef }))
            .sort((a, b) =>
              collator.compare(stripExt(a.name), stripExt(b.name)),
            );
          if (alive) setTaxGuideFiles(files);
        } catch (e) {
          // console.error("Error fetching tax guide files:", e?.message || e);
          if (alive) setTaxGuideFiles([]);
        }
      } else if (alive) {
        setTaxGuideFiles([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [folderName, normalizedEmail]);

  // Load org chart once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const docRef = doc(db, "Marketing", "Website");
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          if (alive) setImageUrl(data["Org Chart"]);
        }
      } catch (e) {
        // no-op
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load permissions (avoid FOIT) and clear when changing folder
  useEffect(() => {
    let alive = true;
    (async () => {
      setPermLoading(true);
      try {
        if (folderName === "Financial Planning") {
          const vtcUsers = await getVTCUsers();
          if (alive) {
            setPermissions({
              // gate this exact subfolder (adjust if your actual subfolder differs)
              "MembersHub/Financial Planning/Venture Capital Trusts": vtcUsers,
            });
          }
        } else {
          if (alive) setPermissions({});
        }
      } catch (e) {
        if (alive) setPermissions({});
        // console.error(e);
      } finally {
        if (alive) setPermLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [folderName]);

  // ---------- PDF helpers (kept as-is) ----------

  const annualGuide = (
    <AnnualGuide
      primaryColor={primaryColor}
      logo={logo}
      tradingName={tradingName}
      regulatoryText={regulatoryText}
      userEmail={normalizedEmail}
    />
  );

  const annuaalGuideV2 = (
    <AnnualGuideV2
      primaryColor={primaryColor}
      logo={logo}
      tradingName={tradingName}
      regulatoryText={regulatoryText}
      userEmail={normalizedEmail}
    />
  );

  const TaxGuidePDF = (
    <TaxGuide
      primaryColor={primaryColor}
      logo={logo}
      tradingName={tradingName}
      regulatoryText={regulatoryText}
      userEmail={normalizedEmail}
    />
  );

  const WhatIOwnAndWhereItIsKeptPDF = (
    <WhatIOwnAndWhereItIsKept
      primaryColor={primaryColor}
      logo={logo}
      tradingName={tradingName}
      regulatoryText={regulatoryText}
      userEmail={normalizedEmail}
      userName={userName}
    />
  );

  const generateAndDownloadWhatIOwnAndWhereItIsKeptPDF = async () => {
    const blob = await pdf(WhatIOwnAndWhereItIsKeptPDF).toBlob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `What I Own And Where It Is Kept.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateAndDownloadAnnualGuidePDF = async () => {
    const blob = await pdf(annualGuide).toBlob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Annual Review Guide ${new Date().toLocaleDateString(
      "en-GB",
      {
        year: "numeric",
      },
    )}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const generateAndDownloadAnnualGuideV2PDF = async () => {
    const blob = await pdf(annuaalGuideV2).toBlob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Annual Review Guide ${new Date().toLocaleDateString(
      "en-GB",
      {
        year: "numeric",
      },
    )}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateAndDownloadTaxGuidePDF = async () => {
    const blob = await pdf(TaxGuidePDF).toBlob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Tax Guide.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---------- Renderer (full-path recursion) ----------

  const renderDirectory = (dir, currentPath) => (
    <div>
      {dir.files.length > 0 && (
        <div className="File-Directory-Container-SubFolderTitle">
          <h4>{currentPath.split("/").pop()}</h4>
          <div className="File-Directory-Container-SubFolder">
            {dir.files.map((file) => (
              <div key={file.name}>
                <PortalCard
                  title={
                    file.name.substring(0, file.name.lastIndexOf(".")) ||
                    file.name
                  }
                  onClick={() => openFileRef(file.ref)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {dir.subfolders.length > 0 && (
        <div className="subfolder-group">
          {dir.subfolders
            .filter((sf) => canSeePath(`${currentPath}/${sf.name}`))
            .map((sf) => (
              <div key={sf.name}>
                {renderDirectory(sf.content, `${currentPath}/${sf.name}`)}
              </div>
            ))}
        </div>
      )}
    </div>
  );

  // ---------- JSX ----------

  return (
    <div className="File-Directory-Page">
      <Sidebar />

      {showPDFModal && canSeeWhiteLabelledDocuments && (
        <PDFViewer style={{ width: "100%", height: "85vh" }} height={1200}>
          {/* <TaxGuide
            primaryColor={primaryColor}
            logo={logo}
            tradingName={tradingName}
            regulatoryText={regulatoryText}
            userEmail={normalizedEmail}
            userName={userName}
          /> */}
          <WhatIOwnAndWhereItIsKept
            primaryColor={primaryColor}
            logo={logo}
            tradingName={tradingName}
            regulatoryText={regulatoryText}
            userEmail={normalizedEmail}
            userName={userName}
          />
        </PDFViewer>
      )}

      <div className="File-Directory-Container">
        {folderName !== "Meet the Team" && (
          <h2>{folderName || "Members Hub"}</h2>
        )}

        {isLoading || permLoading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
            <div className="Content-Small">Loading files...</div>
          </div>
        ) : folderName === "Meet the Team" ? (
          <>
            <img
              className="MeetTheTeam"
              src={imageUrl || ""}
              alt="Meet the Team"
            />
            <ButtonGreen
              buttonText="Read More"
              onClick={() => {
                window.open(
                  "https://rosemountfs-ifa.co.uk/Adviser/AboutUs",
                  "_blank",
                );
              }}
            />
          </>
        ) : directory.subfolders.length > 0 || directory.files.length > 0 ? (
          <>
            {folderName === "Customer Facing Material" && (
              <div className="File-Directory-Container-SubFolderTitle">
                <h4>{WHITE_LABEL_SECTION_TITLE}</h4>

                <div className="File-Directory-Container-SubFolder">
                  {/* Generated PDFs (white-labelled like Annual Guide) */}
                  <PortalCard
                    title="Tax Guide"
                    onClick={generateAndDownloadTaxGuidePDF}
                  />
                  <PortalCard
                    title="What I Own And Where It Is Kept"
                    onClick={generateAndDownloadWhatIOwnAndWhereItIsKeptPDF}
                  />
                  {/* {canSeeWhiteLabelledDocuments && (
                    <> */}
                      {/* <PortalCard
                        title="Annual Review Guide"
                        onClick={generateAndDownloadAnnualGuidePDF}
                      />
                      <PortalCard
                        title="Annual Review Guide V2"
                        onClick={generateAndDownloadAnnualGuidePDF}
                      /> */}
                      {/* <PortalCard
                        title="What I Own And Where It Is Kept"
                        onClick={generateAndDownloadWhatIOwnAndWhereItIsKeptPDF}
                      /> */}
                    {/* </>
                  )} */}

                  {/* Add more generated documents here */}
                  {/* <PortalCard title="Investment Guide" onClick={generateAndDownloadInvestmentGuidePDF} /> */}
                  {/* <PortalCard title="Protection Guide" onClick={generateAndDownloadProtectionGuidePDF} /> */}
                </div>
              </div>
            )}

            {renderDirectory(directory, `MembersHub/${folderName}`)}
          </>
        ) : (
          <div className="FP_Content">Coming Soon...</div>
        )}
      </div>
    </div>
  );
};

export default FileDirectory;
